import { recoverProviderInvoiceBulkPeriods } from '@/utils/accounting-period-setup';
import qs from 'qs';
import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import $utils from '@/utils';
import { executeBulkAction as executeBulk } from "@/utils/bulk";
import { withSelectedIds } from "@/utils/selectionStats";
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';
import {
    PROVIDER_INVOICE_LIST_TO_ITEMS,
    buildInvoiceItemsExportOrdering,
    mapInvoiceListFiltersToItemFilters,
} from '@/utils/invoice-items-export-filters';

const {
    getStorageKey,
    loadFiltersFromStorage,
    saveFiltersToStorage,
    mergeUserAndSectionFilters,
    extractUserFiltersFromEffective,
    getSectionConstraintKeys,
    omitFilterKeys,
} = utils;

/** Shared across all provider-invoice tabs (section constraints applied at query time). */
const PROVIDERINVOICES_FILTERS_STORAGE_KEY = getStorageKey('providerinvoices');
const DEFAULT_USER_FILTERS = {
    offset: 0,
    limit: 20,
    ordering: "-invoice_date",
};

const state = {
    section: null,
    invoices: [],
    /** User-chosen filters persisted across tabs (no section constraints). */
    invoices_user_filters: { ...DEFAULT_USER_FILTERS },
    /** Effective filters for API (user + section overlay). */
    invoices_filters: { ...DEFAULT_USER_FILTERS },
    invoices_user_filters_hydrated: false,
    invoices_count: 0,
    invoices_loading: false,
    invoices_loading_errors: null,
    invoices_cancel_source: null,

    invoices_stats: {},
    invoices_stats_loading: false,
    invoices_stats_loading_errors: null,
    invoices_stats_cancel_source: null,
    selected_items: [],
    bulk_action_loading: false,

    marking_as_paid: false,
};


const modeFilters = {
    draft: {
        status: 'draft',
    },
    waitingpayment: {
        status: 'validated',
        paid: false,
        due: false,
    },
    due: {
        status: 'validated',
        due: true,
        paid: false,
        credit: false,
    },
    paid: {
        status: 'validated',
        paid: true,
    },
    tovalidate: {
        status: 'tovalidate',

    },

    all: {},
};


let getSectionFilters = function (section) {
    return modeFilters[section] || {};
};

function loadSharedProviderInvoicesUserFilters(section) {
    const shared = loadFiltersFromStorage(
        PROVIDERINVOICES_FILTERS_STORAGE_KEY,
        null,
    );
    if (shared) {
        return extractUserFiltersFromEffective(
            shared,
            getSectionFilters(section || 'all'),
            {},
        );
    }
    const legacy = loadFiltersFromStorage(
        getStorageKey('providerinvoices', section || 'all'),
        { ...DEFAULT_USER_FILTERS },
    );
    return extractUserFiltersFromEffective(
        legacy,
        getSectionFilters(section || 'all'),
        {},
    );
}

function persistProviderInvoicesUserFilters(userFilters) {
    saveFiltersToStorage(PROVIDERINVOICES_FILTERS_STORAGE_KEY, userFilters);
}

function rebuildProviderInvoicesFilters(state, { resetOffset = false } = {}) {
    const section = state.section || 'all';
    const previousOffset = state.invoices_filters?.offset ?? 0;
    state.invoices_filters = mergeUserAndSectionFilters(
        state.invoices_user_filters,
        getSectionFilters(section),
    );
    state.invoices_filters.offset = resetOffset ? 0 : previousOffset;
}

const mutations = {
    updateInvoices(state, invoices) {
        state.invoices = invoices;
        // Replace selected_items instances with newly fetched products based on id field
        state.selected_items = state.selected_items.map((selected_item) => {
            return invoices.find((invoice) => invoice.id === selected_item.id) || selected_item;
        });
    },
    updateInvoicesCount(state, invoices_count) {
        state.invoices_count = invoices_count;
    },
    updateInvoicesLoading(state, loading) {
        state.invoices_loading = loading;
    },
    updateInvoicesLoadingErrors(state, errors) {
        state.invoices_loading_errors = errors;
    },
    updateInvoicesFilters(state, invoices_filters) {
        const section = state.section || 'all';
        state.invoices_user_filters = extractUserFiltersFromEffective(
            invoices_filters,
            getSectionFilters(section),
            state.invoices_user_filters,
        );
        state.invoices_user_filters_hydrated = true;
        persistProviderInvoicesUserFilters(state.invoices_user_filters);
        state.invoices_filters = mergeUserAndSectionFilters(
            state.invoices_user_filters,
            getSectionFilters(section),
        );
        state.invoices_filters.offset = invoices_filters?.offset ?? 0;
    },
    updateInvoicesCancelSource(state, source) {
        state.invoices_cancel_source = source;
    },

    updateInvoicesStats(state, invoices_stats) {
        state.invoices_stats = invoices_stats;
    },
    updateInvoicesStatsLoading(state, loading) {
        state.invoices_stats_loading = loading;
    },
    updateInvoicesStatsLoadingErrors(state, errors) {
        state.invoices_stats_loading_errors = errors;
    },
    updateInvoicesStatsCancelSource(state, source) {
        state.invoices_stats_cancel_source = source;
    },
    updateSection(state, section) {
        state.section = section;
        state.invoices = [];
        state.invoices_count = 0;
        state.invoices_loaded = false;

        if (!state.invoices_user_filters_hydrated) {
            state.invoices_user_filters = {
                ...DEFAULT_USER_FILTERS,
                ...loadSharedProviderInvoicesUserFilters(section),
            };
            state.invoices_user_filters_hydrated = true;
            persistProviderInvoicesUserFilters(state.invoices_user_filters);
        }
        rebuildProviderInvoicesFilters(state, { resetOffset: true });
    },
    updateSelectedItems(state, items) {
        state.selected_items = items;
    },
    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },
    updateMarkingAsPaid(state, marking) {
        state.marking_as_paid = marking;
    },
};

const actions = {

    async validateInvoice({ commit, dispatch, state }, invoice) {
        try {
            const response = await axios.post(`/api/providerinvoices/${invoice.id}/validate/`);
            dispatch("fetchInvoices");
            dispatch("session/fetchStats", null, { root: true });
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },


    async bulkAction({ commit, dispatch, state }, params) {
        commit("updateBulkActionLoading", true);
        try {
            const result = await executeBulk({
                bulkActionUrl: "/api/providerinvoices/bulk/",
                action: params.action,
                instances: state.selected_items.map(item => item.id),
                payload: params.payload || {},
                context: params.context,
                items: state.selected_items,
                recoverResult: params.action === "validate" && params.context
                    ? result => recoverProviderInvoiceBulkPeriods(params.context, result, params.payload || {})
                    : undefined,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            commit("updateSelectedItems", []);
            dispatch('fetchInvoices');
            dispatch("session/fetchStats", null, { root: true });
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBulkActionLoading", false);
        }
    },

    async changeSection({ commit, dispatch, state }, mode) {
        if (mode == state.section) {
            return;
        }
        commit("updateSection", mode);
        commit("updateSelectedItems", []);
        if(!["ap-review","ap-payment"].includes(mode)) await dispatch("fetchInvoices");
        dispatch("session/fetchStats", null, { root: true });
    },

    exportInvoicesAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, state.invoices_filters);
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: "/api/providerinvoices/",
            params: filters,
        }, $utils.getFilename('providerInvoices'));
    },

    exportInvoiceItemsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = mapInvoiceListFiltersToItemFilters(
                state.invoices_filters,
                PROVIDER_INVOICE_LIST_TO_ITEMS,
            );
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        filters.ordering = buildInvoiceItemsExportOrdering(
            state.invoices_filters.ordering,
        );

        $utils.downloadFile({
            url: "/api/providerinvoiceitems/",
            params: filters,
        }, $utils.getFilename('providerInvoiceItems'));
    },


    async fetchInvoicesStats({ commit, dispatch, state }, params) {
        commit("updateInvoicesStatsLoading", true);
        commit("updateInvoicesStatsLoadingErrors", null);

        if (state.invoices_stats_cancel_source) {
            state.invoices_stats_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit("updateInvoicesStatsCancelSource", cancelSource);

        try {
            const response = await axios.get("/api/providerinvoices/stats/", {
                params: withSelectedIds(state.invoices_filters, state.selected_items),
                cancelToken: state.invoices_stats_cancel_source?.token,
            });
            commit("updateInvoicesStats", response.data);
            commit("updateInvoicesStatsCancelSource", null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }

            const error = utils.handleError(xhr_error);
            commit("updateInvoicesStatsLoadingErrors", error.details);
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateInvoicesStatsLoading", false);
        }
    },

    async markInvoiceAsPaid({ commit, dispatch, state }, params) {
        try {
            commit("updateMarkingAsPaid", true);
            const response = await axios.post(
                `/api/providerinvoices/${params.invoice.id}/paid/`,
                { paid: true }
            );
            dispatch("fetchInvoices");
            dispatch("session/fetchStats", null, { root: true });
            dispatch("billingstats/fetchAllStats", null, { root: true });
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateMarkingAsPaid", false);
        }
    },

    async fetchInvoices({ commit, dispatch, state }, params) {
        commit("updateInvoicesLoading", true);
        commit("updateInvoicesLoadingErrors", null);

        if (state.invoices_cancel_source) {
            state.invoices_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit("updateInvoicesCancelSource", cancelSource);

        try {
            const response = await axios.get("/api/providerinvoices/", {
                params: state.invoices_filters,
                cancelToken: state.invoices_cancel_source?.token,
            });
            commit("updateInvoices", response.data.results);
            commit("updateInvoicesCount", response.data.count);
            commit("updateInvoicesCancelSource", null);
            commit("updateInvoicesLoading", false);
            dispatch("fetchInvoicesStats");
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }

            const error = utils.handleError(xhr_error);
            commit("updateInvoicesLoadingErrors", error.details);
            commit("updateInvoicesLoading", false);
            throw utils.handleError(xhr_error);
        }
    },
};

const getters = {
    /**
     * Filters bound to Table UI: section constraints are hidden so tabs don't
     * surface forced values as user chips. API still uses invoices_filters.
     */
    invoices_table_filters(state) {
        const section = state.section || 'all';
        return omitFilterKeys(
            state.invoices_filters,
            getSectionConstraintKeys(getSectionFilters(section)),
        );
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
};
